import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, map, of } from 'rxjs';
import { BuscaGlobalApi, ItemBuscaGlobal, TipoItemBuscaGlobal } from 'app/core/dtos/sai/core/busca-global';
import { BuscaGlobalApiService } from 'app/core/services/sai/core/busca-global/busca-global-service.service';
import { CATALOGO_BUSCA_GLOBAL } from './busca-global.catalogo';
import {
  ConsultaBuscaGlobal,
  GrupoResultadoBusca,
  RespostaBuscaGlobal,
  ResultadoBusca,
  TIPOS_RESULTADO_BUSCA,
  TipoResultadoBusca
} from './busca-global.model';
import { normalizarTexto } from './busca-select.util';

const LIMITE_SUGESTOES = 6;
const LIMITE_API_TODOS = 50;
const MINIMO_API = 2;

const PONTOS_TITULO_INICIO = 100;
const PONTOS_TITULO = 80;
const PONTOS_MODULO = 60;
const PONTOS_DESCRICAO = 40;
const PONTOS_TERMOS = 20;

const CARTA = '/adm/carta-servicos';

interface DestinoRegistro {
  tipo: TipoResultadoBusca;
  modulo: string;
  icone: string;
  rota: (item: ItemBuscaGlobal) => string;
  parametros?: (item: ItemBuscaGlobal) => Record<string, string>;
}

/** Para onde cada registro da API leva. Tipo sem destino é ignorado. */
const DESTINOS: Partial<Record<TipoItemBuscaGlobal, DestinoRegistro>> = {
  Servico: {
    tipo: 'Serviço',
    modulo: 'Carta de Serviços',
    icone: 'list-checks',
    rota: (item) => `${CARTA}/servicos/servico/${item.codigo}`
  },
  CategoriaServico: {
    tipo: 'Categoria de serviço',
    modulo: 'Carta de Serviços',
    icone: 'folder',
    rota: (item) => `${CARTA}/servicos/categoria/${item.titulo}`
  },
  Noticia: {
    tipo: 'Notícia',
    modulo: 'Carta de Serviços',
    icone: 'newspaper',
    rota: () => `${CARTA}/noticias/cadastro`,
    parametros: (item) => ({ noticia: String(item.codigo) })
  },
  PedidoSic: {
    tipo: 'Manifestação',
    modulo: 'e-SIC',
    icone: 'inbox',
    rota: () => `${CARTA}/sic/manifestacoes/pedido`,
    parametros: (item) => ({ pedido: String(item.codigo) })
  },
  ManifestacaoOuvidoria: {
    tipo: 'Manifestação',
    modulo: 'Ouvidoria',
    icone: 'message-square',
    rota: () => `${CARTA}/ouvidoria/manifestacoes/detalhe`,
    parametros: (item) => ({ manifestacao: String(item.codigo) })
  }
};

const SEM_REGISTROS: BuscaGlobalApi = { termo: '', total: 0, itens: [] };

/**
 * Busca do topo: as telas do catálogo fixo mais os registros da API, no escopo
 * do usuário. Se a API falhar, a busca segue só com as telas.
 */
@Injectable({ providedIn: 'root' })
export class BuscaGlobalService {
  private readonly router = inject(Router);
  private readonly buscaGlobalApiService = inject(BuscaGlobalApiService);

  buscar(consulta: ConsultaBuscaGlobal): Observable<RespostaBuscaGlobal> {
    const paginas = this.consultarCatalogo(consulta);
    const termo = consulta.termo ?? '';

    return this.consultarApi(termo, consulta.limite ?? LIMITE_API_TODOS).pipe(
      map((api) => {
        const registros = this.filtrar(this.converter(api.itens), consulta);
        const paginasVisiveis = consulta.limite ? paginas.slice(0, consulta.limite) : paginas;
        const filtrado = !!consulta.tipo || !!consulta.modulo;

        return {
          termo,
          total: paginas.length + (filtrado ? registros.length : api.total),
          itens: [...paginasVisiveis, ...registros]
        };
      })
    );
  }

  sugerir(termo: string): Observable<RespostaBuscaGlobal> {
    return this.buscar({ termo, limite: LIMITE_SUGESTOES });
  }

  agrupar(itens: ResultadoBusca[]): GrupoResultadoBusca[] {
    return TIPOS_RESULTADO_BUSCA
      .map((tipo) => ({ tipo, itens: itens.filter((item) => item.tipo === tipo) }))
      .filter((grupo) => grupo.itens.length > 0);
  }

  urlDoResultado(item: ResultadoBusca): string {
    return this.router.serializeUrl(
      this.router.createUrlTree([item.rota], { queryParams: item.parametros })
    );
  }

  private consultarApi(termo: string, limite: number): Observable<BuscaGlobalApi> {
    if (termo.trim().length < MINIMO_API) {
      return of(SEM_REGISTROS);
    }

    return this.buscaGlobalApiService.buscar({ termo, limite }).pipe(
      map((resposta) => resposta.data ?? SEM_REGISTROS),
      catchError(() => of(SEM_REGISTROS))
    );
  }

  private converter(itens: ItemBuscaGlobal[]): ResultadoBusca[] {
    return itens.flatMap((item) => {
      const destino = DESTINOS[item.tipo];

      if (!destino) {
        return [];
      }

      return [{
        id: `${item.tipo}-${item.codigo}`,
        titulo: item.titulo,
        descricao: item.descricao ?? '',
        tipo: destino.tipo,
        modulo: destino.modulo,
        icone: destino.icone,
        rota: destino.rota(item),
        parametros: destino.parametros?.(item),
        atualizadoEm: item.data
      }];
    });
  }

  private filtrar(itens: ResultadoBusca[], consulta: ConsultaBuscaGlobal): ResultadoBusca[] {
    const tipo = consulta.tipo ?? '';
    const modulo = consulta.modulo ?? '';

    return itens
      .filter((item) => !tipo || item.tipo === tipo)
      .filter((item) => !modulo || item.modulo === modulo);
  }

  private consultarCatalogo(consulta: ConsultaBuscaGlobal): ResultadoBusca[] {
    const termo = normalizarTexto(consulta.termo);

    return this.filtrar(CATALOGO_BUSCA_GLOBAL, consulta)
      .map((item) => ({ item, pontos: this.pontuar(item, termo) }))
      .filter((linha) => linha.pontos > 0)
      .sort((a, b) =>
        b.pontos - a.pontos || a.item.titulo.localeCompare(b.item.titulo, 'pt-BR')
      )
      .map((linha) => linha.item);
  }

  private pontuar(item: ResultadoBusca, termo: string): number {
    if (!termo) {
      return 1;
    }

    const titulo = normalizarTexto(item.titulo);

    if (titulo.startsWith(termo)) {
      return PONTOS_TITULO_INICIO;
    }

    if (titulo.includes(termo)) {
      return PONTOS_TITULO;
    }

    if (normalizarTexto(item.modulo).includes(termo)) {
      return PONTOS_MODULO;
    }

    if (normalizarTexto(item.descricao).includes(termo)) {
      return PONTOS_DESCRICAO;
    }

    const achouNosTermos = (item.termos ?? []).some((chave) =>
      normalizarTexto(chave).includes(termo)
    );

    return achouNosTermos ? PONTOS_TERMOS : 0;
  }
}
